/**
 * Session drilldown view — rendered when the user clicks a row in the
 * sessions aggregate table.  Mirrors the reference SessionDrilldown.tsx
 * but works with CoatingRow[] (camelCase) instead of CoatingRecord[].
 *
 * Sections:
 *  1. Session metadata header
 *  2. 5 KPI cards  (session-level)
 *  3. Session timeline  (SVG)
 *  4. Temperature chart + Session flags  (2-col grid)
 *     (Temperature limits are edited on the summary page — values passed in via props.)
 */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine,
} from 'recharts';
import { HelpCircle } from 'lucide-react';
import type { CoatingRow } from './PumpHouseCoatingTable';
import type { SessionSummary, TemperatureLimits, ExcursionCounts } from './dashboardCalculations';
import {
  computeSessionKpis,
  countExcursions,
  isSensorFault,
  fmtDuration,
  compareCoatingRowsChronological,
} from './dashboardCalculations';

// ─── design tokens (shared with SprayDashboardSection) ───────────────────────

const C = {
  blue:    '#00273F',
  red:     '#EB1C37',
  green:   '#1F9A3A',
  yellow:  '#FFCA57',
  grey:    '#969696',
  dkRed:   '#C4001F',
  bg:      '#F5F6F8',
  border:  '#E2E5EA',
  surface: '#FFFFFF',
  surf2:   '#EEF0F3',
  text:    '#464646',
  warnBg:  '#FFF6DE',
  warnTxt: '#B8860B',
};

// ─── helpers ──────────────────────────────────────────────────────────────────

function timeToSecs(t: string): number {
  const p = t.split(':').map(Number);
  return (p[0] ?? 0) * 3600 + (p[1] ?? 0) * 60 + (p[2] ?? 0);
}

function timeDiffSecs(a: string, b: string): number {
  return timeToSecs(b) - timeToSecs(a);
}

function fmtHHMM(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ─── InfoPopover ──────────────────────────────────────────────────────────────

interface InfoContent { title: string; description: string; tip?: string; }

const InfoPopover: React.FC<InfoContent> = ({ title, description, tip }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        style={{
          width: 20, height: 20, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: open ? C.blue : C.surf2, color: open ? '#fff' : C.grey,
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
        }}
      >
        <HelpCircle style={{ width: 11, height: 11 }} strokeWidth={2.5} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 280, zIndex: 50,
          background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12,
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)', overflow: 'hidden',
        }}>
          <div style={{ height: 3, background: C.blue }} />
          <div style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.blue, marginBottom: 6 }}>{title}</div>
            <p style={{ fontSize: 12, color: C.text, lineHeight: 1.6, margin: 0 }}>{description}</p>
            {tip && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.surf2}`, display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 11, color: C.yellow, flexShrink: 0, marginTop: 1 }}>●</span>
                <p style={{ fontSize: 11, color: C.grey, lineHeight: 1.5, margin: 0, fontStyle: 'italic' }}>{tip}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── KpiCard ──────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string; value: React.ReactNode;
  delta?: string; deltaType?: 'up' | 'down' | 'warn' | 'neutral';
  accent?: string; info?: InfoContent;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, delta, deltaType = 'neutral', info }) => {
  const deltaColor = { up: C.green, down: C.dkRed, warn: C.warnTxt, neutral: C.grey }[deltaType];
  return (
    <div
      style={{
        background: '#fff', border: '1px solid #E5E7EB',
        borderRadius: 8, padding: '14px 16px', position: 'relative',
        boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
      }}
    >
      {info && <div style={{ position: 'absolute', top: 8, right: 8 }}><InfoPopover {...info} /></div>}
      <div style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em', paddingRight: info ? 24 : 0 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginTop: 4, letterSpacing: '-0.01em' }}>
        {value}
      </div>
      {delta && (
        <div style={{ fontSize: 11, marginTop: 4, color: deltaColor }}>
          {deltaType === 'up' && '▲ '}{deltaType === 'down' && '▼ '}{delta}
        </div>
      )}
    </div>
  );
};

// ─── Card / CardHeader ────────────────────────────────────────────────────────

interface CardHeaderProps { title: string; subtitle?: string; info?: InfoContent; compact?: boolean; }

const CardHeader: React.FC<CardHeaderProps> = ({ title, subtitle, info, compact }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: compact ? 8 : 12 }}>
    <div>
      <div style={{ fontSize: compact ? 12 : 14, fontWeight: 600, color: '#374151' }}>{title}</div>
      {subtitle && <div style={{ fontSize: compact ? 10 : 11, color: '#6B7280', marginTop: 2 }}>{subtitle}</div>}
    </div>
    {info && <InfoPopover {...info} />}
  </div>
);

interface CardProps { children: React.ReactNode; style?: React.CSSProperties; }

const Card: React.FC<CardProps> = ({ children, style: extra }) => (
  <div
    style={{
      background: '#fff', border: '1px solid #E5E7EB',
      borderRadius: 8, padding: '16px',
      boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)', ...extra,
    }}
  >
    {children}
  </div>
);

// ─── SessionTimeline (SVG) ────────────────────────────────────────────────────

const SessionTimeline: React.FC<{ rows: CoatingRow[] }> = ({ rows }) => {
  const sorted = [...rows].sort(compareCoatingRowsChronological);
  if (sorted.length === 0) return null;

  const joints = sorted.filter(r => !r.isEvent);
  const events = sorted.filter(r => r.isEvent);
  if (joints.length === 0) return null;

  const startSec = timeToSecs(sorted[0].time) - 180;
  const endSec   = timeToSecs(sorted[sorted.length - 1].time) + 180;
  const span     = endSec - startSec || 1;

  const W = 1200;
  const PAD = 14;
  const usable = W - PAD * 2;
  const xPos = (t: string) => PAD + ((timeToSecs(t) - startSec) / span) * usable;
  // Increase rendered width for dense sessions; timeline area can scroll horizontally.
  const minDotGapPx = 22;
  const timelineWidthPx = Math.max(1000, joints.length * minDotGapPx);

  // Time axis labels (~every hour)
  const axisLabels: { x: number; label: string }[] = [];
  const step = Math.max(3600, Math.round(span / 6 / 3600) * 3600);
  const firstTick = Math.ceil(startSec / step) * step;
  for (let s = firstTick; s <= endSec; s += step) {
    axisLabels.push({ x: PAD + ((s - startSec) / span) * usable, label: fmtHHMM(s) });
  }

  /** Gaps shown on timeline only when ≥ 10 min (shorter gaps omitted). */
  const GAP_TIMELINE_MIN_SECS = 600;
  const gaps: { x1: number; x2: number; label: string }[] = [];
  for (let i = 1; i < joints.length; i++) {
    const delta = timeToSecs(joints[i].time) - timeToSecs(joints[i - 1].time);
    if (delta >= GAP_TIMELINE_MIN_SECS) {
      gaps.push({ x1: xPos(joints[i - 1].time), x2: xPos(joints[i].time), label: `${Math.round(delta / 60)}m gap` });
    }
  }

  const dotColor = (r: CoatingRow) => {
    if (isSensorFault(r)) return '#EF9F27';
    if (r.downstrokeStall === 'Fail' || r.upstrokeStall === 'Fail') return C.red;
    return C.green;
  };

  /** Match card horizontal padding so the SVG can bleed edge-to-edge inside the card only. */
  const cardPadX = 18;

  return (
    <Card style={{ marginBottom: 10, padding: `14px ${cardPadX}px` }}>
      <CardHeader
        title="Session timeline"
        subtitle={`${fmtHHMM(startSec + 180)} to ${fmtHHMM(endSec - 180)}`}
        info={{
          title: 'Session Timeline',
          description: 'Visual timeline showing every joint (dot) colour-coded by result, amber bands only for gaps of 10 minutes or longer between joints, and red lines for E-stop events.',
          tip: 'Green = pass · Red/amber = fault or sensor dropout · Yellow band = idletime gap (10+ min).',
        }}
      />
      <div
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        style={{
          marginLeft: -cardPadX,
          marginRight: -cardPadX,
          width: `calc(100% + ${cardPadX * 2}px)`,
          overflowX: 'auto',
          overflowY: 'hidden',
          paddingBottom: 4,
        }}
      >
        <svg
          viewBox={`0 0 ${W} 140`}
          preserveAspectRatio="none"
          style={{ display: 'block', width: timelineWidthPx, minWidth: '100%', height: 140 }}
        >
        {/* Base line */}
        <line x1={PAD} y1={70} x2={W - PAD} y2={70} stroke="#C8CCD3" strokeWidth={0.5} />

        {/* Time axis */}
        {axisLabels.map((t, i) => (
          <text key={i} x={t.x} y={130} textAnchor="middle" fontSize={10} fill={C.grey}>{t.label}</text>
        ))}

        {/* Gap bands */}
        {gaps.map((g, i) => (
          <g key={`gap-${i}`}>
            <rect x={g.x1} y={66} width={g.x2 - g.x1} height={8} fill={C.yellow} opacity={0.6} rx={1} />
            <text x={(g.x1 + g.x2) / 2} y={110} textAnchor="middle" fontSize={9} fill={C.warnTxt}>{g.label}</text>
          </g>
        ))}

        {/* E-stop lines */}
        {events
          .filter(e => e.eventMessage?.includes('Emergency') || e.eventMessage?.includes('E-stop'))
          .map((e, i) => {
            const x = xPos(e.time);
            return (
              <g key={`estop-${i}`}>
                <line x1={x} y1={45} x2={x} y2={95} stroke={C.dkRed} strokeWidth={1.5} />
                <text x={x} y={40} textAnchor="middle" fontSize={9} fill={C.dkRed}>E-stop</text>
              </g>
            );
          })}

        {/* Pressure-transducer storm band */}
        {(() => {
          const storms = events.filter(e => e.eventMessage?.toLowerCase().includes('transducer'));
          if (storms.length > 5) {
            const x1 = xPos(storms[0].time);
            const x2 = Math.min(xPos(storms[storms.length - 1].time), W - PAD);
            const w  = Math.max(x2 - x1, 60);
            return (
              <g>
                <rect x={x1} y={60} width={w} height={20} fill="#FBD2D7" stroke="#E24B4A" strokeWidth={0.5} rx={2} />
                <text x={x1 + w / 2} y={74} textAnchor="middle" fontSize={10} fill={C.dkRed}>
                  Pressure transducer fault storm · {storms.length} events
                </text>
              </g>
            );
          }
          return null;
        })()}

        {/* Joint dots */}
        {joints.map((r, i) => (
          <circle key={`dot-${i}`} cx={xPos(r.time)} cy={70} r={5} fill={dotColor(r)} />
        ))}

        {/* First / last joint labels */}
        {joints.length > 0 && (
          <>
            <text x={xPos(joints[0].time)} y={56} textAnchor="middle" fontSize={9} fill={C.grey}>j{joints[0].joint}</text>
            <text x={xPos(joints[joints.length - 1].time)} y={56} textAnchor="middle" fontSize={9} fill={C.grey}>j{joints[joints.length - 1].joint}</text>
          </>
        )}
        </svg>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: C.grey }}>
        {[
          { color: C.green,  label: 'Pass' },
          { color: C.red,    label: 'Stall fail' },
          { color: '#EF9F27', label: 'Sensor dropout' },
          { color: C.yellow, label: 'Gap ≥ 10 min' },
        ].map(l => (
          <span key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: l.color }} />
            {l.label}
          </span>
        ))}
      </div>
    </Card>
  );
};

// ─── TemperatureChart ─────────────────────────────────────────────────────────

interface TempChartProps { rows: CoatingRow[]; limits: TemperatureLimits | null; compact?: boolean; }

const TempTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.blue, borderRadius: 8, padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Joint {label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>
          {p.name}: {p.value !== null && p.value !== undefined ? `${p.value}°F` : 'dropout'}
        </div>
      ))}
    </div>
  );
};

/** Hollow ring + △ on out-of-limit points (matches `countExcursions`: rows with water 0 skip all excursion markers). */
const ExcursionMarker: React.FC<{ cx?: number; cy?: number; show?: boolean }> = ({ cx, cy, show }) => {
  if (!show || cx == null || cy == null) return null;
  return (
    <g className="recharts-layer">
      <circle cx={cx} cy={cy} r={7} fill="none" stroke={C.warnTxt} strokeWidth={2} />
      <text x={cx} y={cy - 10} textAnchor="middle" fontSize={9} fill={C.warnTxt} style={{ pointerEvents: 'none' }}>△</text>
    </g>
  );
};

const TemperatureChart: React.FC<TempChartProps> = ({ rows, limits, compact }) => {
  const joints = [...rows].filter(r => !r.isEvent).sort(compareCoatingRowsChronological);

  const data = joints.map((r, i) => {
    const wt = parseInt(String(r.waterTemp), 10);
    const bt = parseInt(String(r.baseTemp), 10);
    const ht = parseInt(String(r.hardenerTemp), 10);
    const at = parseInt(String(r.ambientTemp), 10);
    const skipExcursions = wt === 0;
    const excWater =
      !!limits &&
      !skipExcursions &&
      wt !== 0 &&
      Number.isFinite(wt) &&
      (wt < limits.waterMin || wt > limits.waterMax);
    const excBase =
      !!limits &&
      !skipExcursions &&
      Number.isFinite(bt) &&
      (bt < limits.baseMin || bt > limits.baseMax);
    const excHard =
      !!limits &&
      !skipExcursions &&
      Number.isFinite(ht) &&
      (ht < limits.hardenerMin || ht > limits.hardenerMax);
    return {
      label: String(r.joint ?? i + 1),
      water:    wt === 0 ? null : wt,
      base:     Number.isFinite(bt) ? bt : null,
      hardener: Number.isFinite(ht) ? ht : null,
      ambient:  Number.isFinite(at) ? at : null,
      excWater,
      excBase,
      excHard,
    };
  });

  const chartH = compact ? 140 : 200;

  if (data.length === 0) {
    return (
      <Card style={compact ? { padding: '12px' } : undefined}>
        <CardHeader compact={compact} title="Temperatures across joints" subtitle="Base · Hardener" />
        <div style={{ height: compact ? 120 : 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 11, color: C.grey }}>No temperature data</span>
        </div>
      </Card>
    );
  }

  return (
    <Card style={compact ? { padding: '12px' } : undefined}>
      <CardHeader
        compact={compact}
        title="Temperatures across joints"
        subtitle={limits ? 'Base · Hardener · shaded bands = limits · △ = excursion' : 'Base · Hardener'}
        info={{
          title: 'Temperature Chart',
          description: 'Base and hardener temperature per joint. Water and ambient lines are commented out for now.',
          tip: 'When limits are on, shaded bands show the allowed range for base and hardener; a hollow ring with △ marks readings outside those limits (rows with water temp 0 are skipped for excursion markers on base/hardener).',
        }}
      />
      <ResponsiveContainer width="100%" height={chartH}>
        <LineChart data={data} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="2 3" stroke={C.surf2} vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.grey }} axisLine={{ stroke: C.surf2 }} tickLine={false} />
          <YAxis domain={[25, 90]} tick={{ fontSize: 10, fill: C.grey }} axisLine={false} tickLine={false} width={30} tickFormatter={(v: number) => `${v}°`} />
          <Tooltip content={<TempTooltip />} />

          {limits && (
            <>
              <ReferenceArea y1={limits.baseMin}     y2={limits.baseMax}     fill={C.red}    fillOpacity={0.07} ifOverflow="extendDomain" />
              <ReferenceLine y={limits.baseMin}    stroke={C.red}    strokeDasharray="4 3" strokeOpacity={0.5} />
              <ReferenceLine y={limits.baseMax}    stroke={C.red}    strokeDasharray="4 3" strokeOpacity={0.5} />
              <ReferenceArea y1={limits.hardenerMin} y2={limits.hardenerMax} fill={C.yellow} fillOpacity={0.12} ifOverflow="extendDomain" />
              <ReferenceLine y={limits.hardenerMin} stroke={C.yellow} strokeDasharray="4 3" strokeOpacity={0.6} />
              <ReferenceLine y={limits.hardenerMax} stroke={C.yellow} strokeDasharray="4 3" strokeOpacity={0.6} />
              {/*
              FUTURE: Water limit band (when Water line is shown again)
              <ReferenceArea y1={limits.waterMin}    y2={limits.waterMax}    fill={C.blue}   fillOpacity={0.05} ifOverflow="extendDomain" />
              <ReferenceLine y={limits.waterMin}   stroke={C.blue}   strokeDasharray="4 3" strokeOpacity={0.4} />
              <ReferenceLine y={limits.waterMax}   stroke={C.blue}   strokeDasharray="4 3" strokeOpacity={0.4} />
              */}
            </>
          )}

          <Line
            type="monotone"
            dataKey="base"
            stroke={C.red}
            strokeWidth={1.5}
            dot={
              limits
                ? (p: { cx?: number; cy?: number; payload?: { excBase?: boolean } }) => {
                  if (!p.payload?.excBase) return <g />;
                  return <ExcursionMarker cx={p.cx} cy={p.cy} show />;
                }
                : false
            }
            isAnimationActive={false}
            name="Base"
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="hardener"
            stroke={C.yellow}
            strokeWidth={1.5}
            dot={
              limits
                ? (p: { cx?: number; cy?: number; payload?: { excHard?: boolean } }) => {
                  if (!p.payload?.excHard) return <g />;
                  return <ExcursionMarker cx={p.cx} cy={p.cy} show />;
                }
                : false
            }
            isAnimationActive={false}
            name="Hardener"
            connectNulls
          />
          {/*
          FUTURE: Water + Ambient series
          <Line
            type="monotone"
            dataKey="water"
            stroke={C.blue}
            strokeWidth={1.5}
            dot={
              limits
                ? (p: { cx?: number; cy?: number; payload?: { excWater?: boolean } }) => {
                  if (!p.payload?.excWater) return <g />;
                  return <ExcursionMarker cx={p.cx} cy={p.cy} show />;
                }
                : false
            }
            isAnimationActive={false}
            name="Water"
            connectNulls={false}
          />
          <Line type="monotone" dataKey="ambient" stroke={C.grey} strokeWidth={1.5} dot={false} name="Ambient" strokeDasharray="3 2" connectNulls />
          */}
        </LineChart>
      </ResponsiveContainer>

      <div style={{ display: 'flex', gap: 10, marginTop: 6, fontSize: 10, color: C.grey, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { color: C.red,    label: 'Base' },
          { color: C.yellow, label: 'Hardener' },
          // FUTURE: { color: C.blue,   label: 'Water' },
          // FUTURE: { color: C.grey,   label: 'Ambient' },
        ].map(l => (
          <span key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ display: 'inline-block', width: 10, height: 2, background: l.color, borderRadius: 1 }} />
            {l.label}
          </span>
        ))}
        {limits && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.warnTxt, fontWeight: 500 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', border: `2px solid ${C.warnTxt}`, flexShrink: 0 }} />
            △ Out of limits
          </span>
        )}
      </div>
    </Card>
  );
};

// ─── SessionFlags (auto-derived) ──────────────────────────────────────────────

type FlagSeverity = 'danger' | 'warn' | 'ok';
interface AutoFlag { title: string; detail: string; severity: FlagSeverity; }

const FLAG_STYLES: Record<FlagSeverity, React.CSSProperties> = {
  danger: { background: '#FBD2D7', color: C.dkRed },
  warn:   { background: C.warnBg,  color: C.warnTxt },
  ok:     { background: '#E4F5E8', color: '#1F9A3A' },
};

const FLAG_ICONS: Record<FlagSeverity, string> = { danger: '✕', warn: '△', ok: '✓' };

const FLAG_SEVERITY_ORDER: Record<FlagSeverity, number> = { danger: 0, warn: 1, ok: 2 };

function buildSessionFlags(
  rows: CoatingRow[],
  stallFails: number,
  gapCount: number,
  fpYield: number,
  excursions?: ExcursionCounts,
  limits?: TemperatureLimits,
): AutoFlag[] {
  const jointOnly = rows.filter(r => !r.isEvent);
  const flags: AutoFlag[] = [];

  if (stallFails > 0) {
    flags.push({ title: 'Stall failures detected', detail: `${stallFails} upstroke / downstroke stall failure${stallFails > 1 ? 's' : ''}`, severity: 'danger' });
  }

  const sensorFaults = jointOnly.filter(r => isSensorFault(r)).length;
  if (sensorFaults > 0) {
    flags.push({ title: 'Pressure transducer dropout', detail: `${sensorFaults} joint${sensorFaults > 1 ? 's' : ''} recorded zero water-temp (sensor fault)`, severity: 'warn' });
  }

  const flushFails = jointOnly.filter(r => r.systemFlushed && r.systemFlushed !== 'Flush OK').length;
  if (flushFails > 0) {
    flags.push({ title: 'Flush errors', detail: `${flushFails} joint${flushFails > 1 ? 's' : ''} – flush not OK`, severity: 'warn' });
  }

  const primeFails = jointOnly.filter(r => r.systemPrimed && r.systemPrimed !== 'Prime OK').length;
  if (primeFails > 0) {
    flags.push({ title: 'Prime errors', detail: `${primeFails} joint${primeFails > 1 ? 's' : ''} – prime not OK`, severity: 'warn' });
  }

  if (gapCount > 0) {
    flags.push({ title: 'Idletime gaps', detail: `${gapCount} gap${gapCount > 1 ? 's' : ''} longer than 5 minutes detected`, severity: 'warn' });
  }

  const events = rows.filter((r) => r.isEvent);
  const eStops = events.filter(
    (r) => r.eventMessage?.includes('Emergency') || r.eventMessage?.includes('E-stop'),
  ).length;
  if (eStops > 0) {
    flags.push({
      title: 'Emergency stop',
      detail: `${eStops} E-stop event${eStops > 1 ? 's' : ''} logged`,
      severity: 'danger',
    });
  }
  const transducerEvents = events.filter((r) =>
    r.eventMessage?.toLowerCase().includes('transducer'),
  ).length;
  if (transducerEvents > 0) {
    flags.push({
      title: 'Pressure transducer alerts',
      detail: `${transducerEvents} transducer-related event${transducerEvents > 1 ? 's' : ''} logged`,
      severity: 'warn',
    });
  }

  // Excursion flags — only shown when limits are active
  if (excursions && limits) {
    if (excursions.water > 0) {
      flags.push({ title: `△ Water temp excursion on ${excursions.water} joint(s)`, detail: `Outside ${limits.waterMin}–${limits.waterMax}°F operating limit`, severity: 'warn' });
    }
    if (excursions.base > 0) {
      flags.push({ title: `△ Base temp excursion on ${excursions.base} joint(s)`, detail: `Outside ${limits.baseMin}–${limits.baseMax}°F operating limit`, severity: 'warn' });
    }
    if (excursions.hard > 0) {
      flags.push({ title: `△ Hardener temp excursion on ${excursions.hard} joint(s)`, detail: `Outside ${limits.hardenerMin}–${limits.hardenerMax}°F operating limit`, severity: 'warn' });
    }
  }

  if (fpYield >= 95) {
    flags.push({ title: 'High first-pass yield', detail: `${fpYield}% of joints passed on the first attempt`, severity: 'ok' });
  } else if (flags.length === 0) {
    flags.push({ title: 'No critical issues', detail: 'All checks passed for this session', severity: 'ok' });
  }

  flags.sort((a, b) => FLAG_SEVERITY_ORDER[a.severity] - FLAG_SEVERITY_ORDER[b.severity]);
  return flags;
}

// ─── SessionDrilldownSection ──────────────────────────────────────────────────

interface Props {
  /** Joint rows for the table / chart (filtered); usually excludes events when `showEvents` is off. */
  rows: CoatingRow[];
  /**
   * When set: merged joint rows + session events for timeline, KPI fault/E-stop counts, and session flags.
   * Falls back to `rows` when omitted.
   */
  timelineRows?: CoatingRow[];
  session: SessionSummary;
  /** Limits from parent (summary page) — survives closing the drilldown modal. */
  drillTemperatureLimits: TemperatureLimits;
  drillTemperatureLimitsActive: boolean;
}

const SessionDrilldownSection: React.FC<Props> = ({
  rows,
  timelineRows,
  session,
  drillTemperatureLimits,
  drillTemperatureLimitsActive,
}) => {
  const limits = drillTemperatureLimits;
  const limitsActive = drillTemperatureLimitsActive;
  const rowsForTimeline = timelineRows ?? rows;

  const kpis = useMemo(() => computeSessionKpis(rowsForTimeline), [rowsForTimeline]);

  /** Excursion counts only when user-applied limits are active; otherwise show zero (no default-band KPI). */
  const excursions = useMemo(() => {
    if (!limitsActive) return { total: 0, water: 0, base: 0, hard: 0 };
    return countExcursions(rowsForTimeline, limits);
  }, [rowsForTimeline, limits, limitsActive]);

  const flags = useMemo(
    () => buildSessionFlags(
      rowsForTimeline,
      kpis.stallFails,
      kpis.gapCount,
      kpis.fpYield,
      limitsActive ? excursions : undefined,
      limitsActive ? limits : undefined,
    ),
    [rowsForTimeline, kpis.stallFails, kpis.gapCount, kpis.fpYield, excursions, limits, limitsActive],
  );

  // Session metadata (first joint record)
  const firstRow = rows.filter(r => !r.isEvent)[0];
  const operator    = firstRow?.user         || session.user;
  const coatingType = firstRow?.coatingType  || firstRow?.coatingMaterial || session.coatingType;
  const prime       = firstRow?.prime        || '—';
  const flush       = firstRow?.flush        || '—';

  return (
    <div style={{ marginBottom: 24 }}>

      {/* ── KPI row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 14 }}>
        <KpiCard
          label="Joints attempted" value={kpis.totalAttempts}
          delta={`${kpis.uniqueJoints} unique · ${kpis.retryText}`} deltaType="neutral"
          info={{ title: 'Joints Attempted', description: 'Total spray attempts including retries. "Unique" is how many distinct calendar day + joint targets appear (same joint on another day counts again).', tip: 'A retry count > 0 usually indicates a stall or sensor issue on the first attempt.' }}
        />
        <KpiCard
          label="First-pass yield"
          value={(
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' }}>{kpis.fpYield}%</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginTop: 6 }}>
                Total yield {kpis.totalYield}%
              </div>
            </div>
          )}
          delta={kpis.stallFails > 0 ? `${kpis.stallFails} stall fail` : 'All passed'} deltaType={kpis.stallFails > 0 ? 'warn' : 'up'}
          info={{
            title: 'First-pass yield & total yield',
            description:
              'First-pass yield: share of first attempts (per calendar day and joint) where every gate passed — downstroke Pass, upstroke Pass, Flush OK, Prime OK — and the row is not a water-temp sensor fault. Total yield: same buckets; a joint counts as pass if any attempt in that bucket meets those checks (retries can recover a joint).',
            tip: 'First attempt with a sensor fault counts in the denominator for first-pass yield but cannot count as a pass; a later successful retry can lift total yield.',
          }}
        />
        <KpiCard
          label="Median spray cycle" value={kpis.medianCycleStr}
          delta={`j1 to j${rows.filter(r => !r.isEvent).slice(-1)[0]?.joint ?? '?'}, gaps excluded`} deltaType="neutral"
          info={{ title: 'Median Spray Cycle', description: 'The typical time between completing one joint and the next. Gaps > 5 min are treated as idletime and excluded.', tip: 'Median avoids distortion from one long break.' }}
        />
        <KpiCard
          label="Idletime" value={kpis.downtimeStr}
          delta={kpis.gapCount > 0 ? `${kpis.gapCount} gaps > 5 min` : 'No gaps detected'} deltaType={kpis.gapCount > 0 ? 'warn' : 'up'}
          info={{ title: 'Idletime', description: 'Sum of gaps longer than 5 minutes between consecutive joints (idletime).', tip: 'Check the joint log to see exactly where each gap occurred.' }}
        />
        <KpiCard
          label="Fault events" value={kpis.faultEvents}
          delta={kpis.eStops > 0 ? `${kpis.eStops} E-stop${kpis.eStops > 1 ? 's' : ''}` : 'No E-stops'} deltaType={kpis.faultEvents > 0 ? 'down' : 'neutral'}
          info={{ title: 'Fault events', description: 'Total non-joint event rows recorded during this session, broken down by type in the timeline — this is the raw event count. A sensor storm of 122 separate log lines shows as 122 here, even when that represents one operational incident.', tip: 'Joint stall / flush / prime issues are in other KPIs and session flags. The subtitle is how many of these event rows mention Emergency or E-stop.' }}
        />
        <KpiCard
          label="Excursions"
          value={excursions.total}
          {...(() => {
            const parts = [
              excursions.water > 0 ? `${excursions.water} water` : '',
              excursions.base  > 0 ? `${excursions.base} base` : '',
              excursions.hard  > 0 ? `${excursions.hard} hardener` : '',
            ].filter(Boolean);
            return parts.length ? { delta: parts.join(' · '), deltaType: 'warn' as const } : {};
          })()}
          info={{
            title: 'Temperature Excursions',
            description: 'Per joint: counts readings outside min/max for water, base, and hardener when limits are active. Joints with water temp 0 (sensor dropout) are skipped entirely for all three sensors.',
            tip: limitsActive
              ? 'Using limits applied on the pump house summary page.'
              : 'Enable and save limits on the pump house summary page to see excursion counts here.',
          }}
        />
      </div>

      {/* ── session timeline ── */}
      <SessionTimeline rows={rowsForTimeline} />

      {/* ── temperature chart + session flags (compact; flags scroll) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.45fr minmax(200px, 0.9fr)', gap: 8, marginBottom: 4, alignItems: 'stretch' }}>
        <TemperatureChart compact rows={rows} limits={limitsActive ? limits : null} />

        <Card style={{ padding: '12px', display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: 240 }}>
          <CardHeader
            compact
            title="Session flags"
            info={{ title: 'Session Flags', description: 'Automatically derived quality flags for this session based on stall failures, sensor dropouts, flush/prime errors, and idletime gaps.', tip: 'Green flags indicate the session met all quality thresholds.' }}
          />
          <div
            className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent pr-1"
            style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}
          >
            {flags.map((f, i) => (
              <div key={i} style={{ ...FLAG_STYLES[f.severity], borderRadius: 6, padding: '6px 10px', fontSize: 11, flexShrink: 0 }}>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, lineHeight: 1.25 }}>
                  <span style={{ fontSize: 10 }}>{FLAG_ICONS[f.severity]}</span>
                  {f.title}
                </div>
                <div style={{ fontSize: 10, marginTop: 2, opacity: 0.88, lineHeight: 1.35 }}>{f.detail}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

    </div>
  );
};

export default SessionDrilldownSection;
